<template>
    <v-dialog v-model="internalDialog" max-width="600" persistent>
        <v-card>
            <v-card-title class="text-h5">
                Edit Company
                <v-spacer></v-spacer>
                <v-btn icon @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <v-form ref="form" v-model="valid">
                    <!-- Company Name -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Company Name <span class="red--text">*</span>
                        </label>
                        <v-text-field v-model="formData.name" placeholder="Enter company name" outlined dense
                            :rules="[rules.required]"></v-text-field>
                    </div>

                    <!-- Description -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Description
                        </label>
                        <v-textarea v-model="formData.description"
                            placeholder="Brief description of the company or project" outlined dense rows="3"
                            auto-grow></v-textarea>
                    </div>

                    <!-- Website -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Website
                        </label>
                        <v-text-field v-model="formData.website"
                            placeholder="Enter company website" outlined dense :rules="[rules.url]"></v-text-field>
                    </div>



                    <!-- Status -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Status
                        </label>
                        <v-select v-model="formData.status" :items="statusOptions" placeholder="Active" outlined
                            dense></v-select>
                    </div>

                    <!-- Due Date -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Due Date
                        </label>
                        <v-menu
                            v-model="dueDateMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="formData.due_date"
                                    placeholder="Select due date"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    outlined
                                    dense
                                    v-bind="attrs"
                                    v-on="on"
                                    clearable
                                    @click:clear="formData.due_date = null"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="formData.due_date"
                                @input="dueDateMenu = false"
                                no-title
                                scrollable
                            ></v-date-picker>
                        </v-menu>
                    </div>

                    <!-- Team Members -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Team Members
                        </label>
                        <v-autocomplete v-model="formData.team_members" :items="usersList" item-text="name" item-value="id"
                            placeholder="Add team member" outlined dense multiple chips small-chips deletable-chips>
                            <template v-slot:selection="{ item, index }">
                                <v-chip v-if="index < 3" small close @click:close="removeTeamMember(item.id)">
                                    {{ item.name }}
                                </v-chip>
                                <span v-if="index === 3" class="grey--text text-caption">
                                    (+{{ formData.team_members.length - 3 }} others)
                                </span>
                            </template>
                        </v-autocomplete>
                    </div>
                </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-4">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog" :disabled="loading">
                    Cancel
                </v-btn>
                <v-btn color="primary" @click="updateCompany" :loading="loading" :disabled="!valid">
                    Update Company
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'EditCompanyDialog',
    inject: ['users', 'optimisticUpdateCompany'],
    props: {
        value: {
            type: Boolean,
            default: false
        },
        company: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            internalDialog: this.value,
            valid: false,
            loading: false,
            dueDateMenu: false,
            formData: {
                name: '',
                description: '',
                status: 'Active',
                website: '',
                due_date: null,
                team_members: []
            },
            statusOptions: ['Active', 'On-Hold', 'Closed'],
            rules: {
                required: (v) => !!v || 'This field is required'
                ,
                url: (v) => {
                    if (!v) return true; // allow empty
                    const pattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i;
                    return pattern.test(v) || 'Enter a valid URL (https://...)';
                }
            }
        };
    },
    computed: {
        usersList() {
            return this.users() || [];
        }
    },
    watch: {
        value(val) {
            this.internalDialog = val;
            if (val) {
                this.populateForm();
            }
        },
        internalDialog(val) {
            this.$emit('input', val);
            if (!val) {
                this.resetForm();
            }
        },
        company: {
            deep: true,
            handler() {
                if (this.internalDialog) {
                    this.populateForm();
                }
            }
        }
    },
    methods: {
        closeDialog() {
            this.internalDialog = false;
        },
        populateForm() {
            if (this.company && this.company.id) {
                // Extract user_id from team_members array of objects
                let teamMemberIds = [];
                if (this.company.team_members && Array.isArray(this.company.team_members)) {
                    teamMemberIds = this.company.team_members.map(member => {
                        // Handle both object format {user_id: 3} and direct ID format
                        return typeof member === 'object' ? member.user_id : member;
                    }).filter(id => id); // Remove any undefined/null values
                }
                
                this.formData = {
                    name: this.company.name || '',
                    description: this.company.description || '',
                    status: this.company.status || 'Active',
                    website: this.company.website || '',
                    due_date: this.company.due_date || null,
                    team_members: teamMemberIds
                };
            }
        },
        removeTeamMember(memberId) {
            const index = this.formData.team_members.indexOf(memberId);
            if (index > -1) {
                this.formData.team_members.splice(index, 1);
            }
        },
        resetForm() {
            this.formData = {
                name: '',
                description: '',
                status: 'Active',
                website: '',
                due_date: null,
                team_members: []
            };
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
        async updateCompany() {
            if (!this.$refs.form.validate()) {
                return;
            }

            if (!this.company || !this.company.id) {
                this.$toast.error('No company selected');
                return;
            }

            this.loading = true;
            try {
                await this.optimisticUpdateCompany(
                    this.company.id,
                    this.formData,
                    async () => {
                        const response = await this.$axios.post(`api/companies/update/${this.company.id}`, this.formData);
                        
                        if (!response.data.response) {
                            throw new Error(response.data.message || 'Failed to update company');
                        }
                        
                        return response.data;
                    }
                );

                this.$emit('company-updated', { ...this.company, ...this.formData });
                this.closeDialog();
            } catch (error) {
                console.error('Error updating company:', error);
                this.$toast.error(error.message || 'An error occurred while updating the company');
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.v-card-title {
    background-color: #f5f5f5;
}

label {
    color: #333;
}
</style>
